import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, m } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { cn } from "../cn";
import { springSoft, useMotionTransition } from "../motion";

export interface LightboxItem {
  /** Full-resolution source URL. */
  readonly src: string;
  /** Accessible description / caption for the image. */
  readonly alt: string;
}

export interface LightboxProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Ordered images to page through. */
  readonly items: readonly LightboxItem[];
  /** Index of the image to show first / when opened. Defaults to 0. */
  readonly index?: number;
  /** Notifies the consumer when the visible image changes. */
  readonly onIndexChange?: (index: number) => void;
  /**
   * Element to portal into. Defaults to `document.body`; pass the themed root
   * when `.ui-v2-theme` is scoped to a wrapper rather than set on `<html>`.
   */
  readonly container?: HTMLElement | null;
}

/**
 * Full-bleed image viewer built on the same Radix Dialog foundation as the
 * `Dialog` primitive — inheriting its focus trap, escape-to-close, scroll lock
 * and framer enter/exit — but without the titled card chrome, so the image
 * owns the whole surface. Adds keyboard (←/→) and on-screen paging across a
 * set of images.
 */
export function Lightbox({
  open,
  onOpenChange,
  items,
  index = 0,
  onIndexChange,
  container,
}: LightboxProps) {
  const [current, setCurrent] = useState(index);

  // Re-sync when the consumer opens the viewer at a specific image.
  useEffect(() => {
    if (open) {
      setCurrent(index);
    }
  }, [open, index]);

  const count = items.length;

  const go = useCallback(
    (next: number) => {
      if (count === 0) {
        return;
      }
      const wrapped = (next + count) % count;
      setCurrent(wrapped);
      onIndexChange?.(wrapped);
    },
    [count, onIndexChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        go(current - 1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        go(current + 1);
      }
    },
    [current, go],
  );

  const active = items[current];
  const hasMultiple = count > 1;
  const overlayTransition = useMotionTransition({ duration: 0.2 });
  const contentTransition = useMotionTransition(springSoft);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && active && (
          <DialogPrimitive.Portal forceMount container={container}>
            <DialogPrimitive.Overlay asChild forceMount>
              <m.div
                className="fixed inset-0 z-50 bg-foreground/80 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={overlayTransition}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content
              asChild
              forceMount
              onKeyDown={handleKeyDown}
            >
              <m.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={contentTransition}
                className="fixed inset-0 z-50 flex flex-col focus:outline-none"
              >
                {/* Accessible name/description for screen readers. Radix
                    requires a Title on DialogContent; it is visually hidden
                    because the viewer is a full-bleed image surface. */}
                <DialogPrimitive.Title className="sr-only">
                  {active.alt || "Afbeelding"}
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="sr-only">
                  Afbeelding {current + 1} van {count}. Gebruik de
                  pijltjes­toetsen om te navigeren.
                </DialogPrimitive.Description>

                {/* Top bar: counter + close */}
                <div className="flex items-center justify-between p-4 text-background">
                  <span className="rounded-full bg-background/10 px-3 py-1 text-sm tabular-nums">
                    {current + 1} / {count}
                  </span>
                  <DialogPrimitive.Close
                    className="grid size-10 place-items-center rounded-full text-background/80 transition-colors hover:bg-background/10 hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="Sluiten"
                  >
                    <X className="size-5" />
                  </DialogPrimitive.Close>
                </div>

                {/* Stage */}
                <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-4">
                  {hasMultiple && (
                    <button
                      type="button"
                      onClick={() => go(current - 1)}
                      aria-label="Vorige afbeelding"
                      className="absolute left-4 grid size-11 place-items-center rounded-full bg-background/10 text-background/80 transition-colors hover:bg-background/20 hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <ChevronLeft className="size-6" />
                    </button>
                  )}

                  <img
                    key={active.src}
                    src={active.src}
                    alt={active.alt}
                    className="max-h-full max-w-full object-contain"
                  />

                  {hasMultiple && (
                    <button
                      type="button"
                      onClick={() => go(current + 1)}
                      aria-label="Volgende afbeelding"
                      className="absolute right-4 grid size-11 place-items-center rounded-full bg-background/10 text-background/80 transition-colors hover:bg-background/20 hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <ChevronRight className="size-6" />
                    </button>
                  )}
                </div>

                {/* Caption */}
                {active.alt && (
                  <p
                    className={cn(
                      "mx-auto max-w-2xl truncate px-4 pb-4 text-center text-sm text-background/70",
                    )}
                  >
                    {active.alt}
                  </p>
                )}
              </m.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
